import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Grid, Card, CardHeader, CardContent, Modal, Box, Divider, Tooltip, Hidden, Typography, IconButton, Toolbar, Chip } from '@mui/material';
import {
  UserCheck as UserCheckIcon,
  Edit as EditIcon
} from 'react-feather';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';
import GlobalSearch from '../GlobalSearch';
import FilterBy from '../FilterBy';
import Table from '../Table';
import MobileDataList from '../MobileDataList';
import { formatDateToLongDate, formatDateToCalendarTime, isDriver, isOwner, isManager, getJobSiteName, formatDateToOnlyTime, formatDateToOnlyDate } from '../../utils/common';
import LocalStorageService from '../../services/LocalStorageService';
import FormattedShift from '../../models/FormattedShift';
import MultiRowActionButton from '../MultiRowActionButton';
import EnumService from '../../services/EnumService';
import { EnumRes } from '../../proxy/proxy';

interface Props {
  shifts: Array<FormattedShift>,
  totalRowCount: number,
  currentPage: number;
  searchedText: string,
  sortModel: any,
  selectedWarning: string,
  onFilterByText: any,
  onFilterByWarning: any,
  filterByCol: any,
  onPageChange: any,
  onSort: any,
  onChangeFilterByColumn: any,
  shiftStatus: number,
  selectionModel?: any,
  onSelectionModelChange?: any,
  selectableRows?: boolean,
  onBulkDelete?: any
}

const ShiftTable: React.FC<Props> = ({
  shifts = [],
  totalRowCount,
  currentPage,
  sortModel,
  searchedText,
  selectedWarning,
  filterByCol,
  shiftStatus,
  selectableRows = false,
  onFilterByText,
  onFilterByWarning,
  onPageChange,
  onSort,
  onChangeFilterByColumn,
  selectionModel,
  onSelectionModelChange = () => {},
  onBulkDelete = () => {},
}) => {
  const navigate = useNavigate();

  const localStorageService = LocalStorageService.getService();
  const roles = localStorageService.getUserRoles();

  const [appUsageTypes, setAppUsageTypes] = React.useState<EnumRes[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedSummary, setSelectedSummary] = useState<string>('');

  const handleOpenImage = (imageSrc: string) => {
    setSelectedImage(imageSrc);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  useEffect(() => {
    let isMounted = true;

    EnumService.getEnums('appUsagePatterns').then((data) => {
      if (isMounted) {
        setAppUsageTypes(data);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const onRowClick = ({ id, extCompany }: any) => {
    if (isDriver(roles)) {
      if ((shiftStatus === 1 || shiftStatus === 2) && extCompany?.companyId) {
        navigate(`/employee/shift/${id}/submit`);
      } else {
        navigate(`/employee/shift/${id}`);
      }
    } else {
      navigate(`/app/shift/list/${id}`);
    }
  };

  const getDriverAcceptance = (userHasAccepted: boolean, withStyles = false) => {
    if (userHasAccepted === true) return 'Accepted';
    if (userHasAccepted === false) {
      if (withStyles) return <span style={{ fontWeight: 'bold', color: 'red', fontSize: '1rem' }}>Rejected</span>;
      return 'Rejected';
    }
    return 'Pending';
  };

  const getTimsheetStatusTooltip = (shift: FormattedShift) => {
    if (shift === null || shift.shiftNRs === null || shift.shiftNRs.length === 0) return 'No Timesheet submitted';
    if (shift.shiftNRs.length === 1) return '1 Timesheet submitted';
    return `${shift.shiftNRs.length} Timsheets submitted`;
  };

  const getAppUsagePatternTooltip = (shift: FormattedShift) => {
    if (appUsageTypes.find((type) => type.id === shift?.user?.appUsagePattern)?.id === null || appUsageTypes.find((type) => type.id === shift?.user?.appUsagePattern)?.id === undefined) return 'No status provided';
    if (appUsageTypes.find((type) => type.id === shift?.user?.appUsagePattern)?.id === 0) return 'The user will have the capability to access the trucksight mobile app to manage shifts and no sms notifications will be sent';
    if (appUsageTypes.find((type) => type.id === shift?.user?.appUsagePattern)?.id === 1) return 'The user will exclusively receive SMS notifications regarding shifts, they won`t have acess to the mobile application';
    return 'No status provided';
  };

  const columns = [
    {
      field: 'scheduledStartDateUTC',
      headerName: 'Date',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Scheduled date of the shift">
          <span style={{ fontWeight: '600' }}>Date</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value, row } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(row.scheduledStartUTC, true) || ''}>
            <span>
              {formatDateToOnlyDate(row.scheduledStartUTC, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'amOrPm',
      headerName: 'Shift',
      minWidth: 70,
      maxWidth: 80,
      filterable: true,
      renderHeader: () => (
        <Tooltip title="AM or PM shift indicator">
          <span style={{ fontWeight: '600' }}>Shift</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value, row } = params;
        return (
          <Tooltip title={value}>
            <span>{value || '-'}</span>
          </Tooltip>
        );
      }
    },
    {
      field: 'dock',
      headerName: 'Dock',
      flex: 1,
      minWidth: 100,
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Loading dock location">
          <span style={{ fontWeight: '600' }}>Dock</span>
        </Tooltip>
      ),
    },
    {
      field: 'jobsiteName',
      headerName: 'Store',
      flex: 1,
      minWidth: 100,
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Store or jobsite location name">
          <span style={{ fontWeight: '600' }}>Store</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={value || ''}>
            <span>{value}</span>
          </Tooltip>
        );
      }
    },
    ...(isOwner(roles) || isManager(roles) ? [
      {
        field: 'userName',
        headerName: 'Driver',
        flex: 1,
        minWidth: 100,
        filterable: false,
        renderHeader: () => (
          <Tooltip title="Name of the assigned driver">
            <span style={{ fontWeight: '600' }}>Driver</span>
          </Tooltip>
        ),
        renderCell: (params: any) => {
          const { value, row } = params;
          return (
            <Tooltip title={value || row.userEmail || ''}>
              <span>{value || row.userEmail}</span>
            </Tooltip>
          );
        }
      },
      {
        field: 'userPhone',
        headerName: 'Mobile',
        flex: 1,
        minWidth: 100,
        filterable: false,
        renderHeader: () => (
          <Tooltip title="Mobile number of the assigned driver">
            <span style={{ fontWeight: '600' }}>Mobile</span>
          </Tooltip>
        ),
        renderCell: (params: any) => {
          const { value } = params;
          return (
            <Tooltip title={value || ''}>
              <span>{value}</span>
            </Tooltip>
          );
        },
      },
      {
        field: 'userExtDriverId',
        headerName: 'CSA ID',
        width: 80,
        filterable: false,
        renderHeader: () => (
          <Tooltip title="Ext Driver ID">
            <span style={{ fontWeight: '600' }}>CSA ID</span>
          </Tooltip>
        ),
        renderCell: (params: any) => {
          const { value } = params;
          return (
            <Tooltip title={value || ''}>
              <span>
                {value}
              </span>
            </Tooltip>
          );
        }
      }
    ] : []),
    ...(isDriver(roles) && shiftStatus === 1 && shifts.find((shift) => shift.isNew) ? [
      {
        field: 'isNew',
        renderHeader: () => <span />,
        type: 'boolean',
        width: 70,
        filterable: false,
        renderCell: ({ value }: any) => {
          if (value) {
            return <span style={{ backgroundColor: 'green', color: 'white', borderRadius: '5px', lineHeight: 1, padding: '5px', fontSize: '0.75rem' }}>New</span>;
          }
          return <span />;
        }
      }
    ] : []),
    {
      field: 'vehicleRego',
      headerName: 'Vehicle',
      width: 100,
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Vehicle registration number">
          <span style={{ fontWeight: '600' }}>Vehicle</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value, row } = params;
        return (
          <Tooltip title={value || ''}>
            <span>
              {value || `${row.unlistedVehicleRego} (Not listed)`}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'stateCode',
      headerName: 'State',
      hide: true,
      renderHeader: () => (
        <Tooltip title="State where the jobsite is located">
          <span style={{ fontWeight: '600' }}>State</span>
        </Tooltip>
      ),
      renderCell: ({ row }: {row: any}) => row?.jobsite?.address?.state?.value ?? '',
      width: 100
    },
    {
      field: 'appUsagePattern',
      headerName: 'App Usage',
      hide: true,
      renderHeader: () => (
        <Tooltip title="App Usage Pattern">
          <span style={{ fontWeight: '600' }}>App Usage</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value, row } = params;
        return (
          <Tooltip title={getAppUsagePatternTooltip(row)}>
            <span>
              {appUsageTypes.find((type) => type.id === row?.user?.appUsagePattern)?.value ?? ''}
            </span>
          </Tooltip>
        );
      },
      width: 100
    },
    {
      field: 'scheduledStartUTC',
      headerName: 'Start',
      flex: 1,
      minWidth: 70,
      maxWidth: 80,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Scheduled start time of the shift">
          <span style={{ fontWeight: '600' }}>Start</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
            <span>
              {formatDateToOnlyTime(value, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'scheduledEndUTC',
      headerName: 'End',
      flex: 1,
      minWidth: 70,
      maxWidth: 80,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Scheduled end time of the shift">
          <span style={{ fontWeight: '600' }}>End</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
            <span>
              {value && formatDateToOnlyTime(value, true)}
            </span>
          </Tooltip>
        );
      }
    },
    ...(shiftStatus !== 1 ? [
      {
        field: 'startedOnUTC',
        headerName: 'Actual Start',
        flex: 1,
        minWidth: 70,
        maxWidth: 80,
        type: 'date',
        filterable: false,
        renderHeader: () => (
          <Tooltip title="Actual time when the shift started">
            <span style={{ fontWeight: '600' }}>Actual Start</span>
          </Tooltip>
        ),
        renderCell: (params: any) => {
          const { value } = params;
          return (
            <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
              <span>
                {formatDateToOnlyTime(value, true)}
              </span>
            </Tooltip>
          );
        }
      }
    ] : []),
    ...(shiftStatus === 3 ? [
      {
        field: 'endedOnUTC',
        headerName: 'Actual End',
        flex: 1,
        minWidth: 70,
        maxWidth: 80,
        type: 'date',
        filterable: false,
        renderHeader: () => (
          <Tooltip title="Actual time when the shift ended">
            <span style={{ fontWeight: '600' }}>Actual End</span>
          </Tooltip>
        ),
        renderCell: (params: any) => {
          const { value } = params;
          return (
            <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
              <span>
                {value && formatDateToOnlyTime(value, true)}
              </span>
            </Tooltip>
          );
        }
      },
    ] : []),
    ...(shiftStatus === 1 ? [
      {
        field: 'userHasAccepted',
        headerName: 'Acceptance',
        type: 'boolean',
        flex: 1,
        minWidth: 100,
        filterable: false,
        renderHeader: () => (
          <Tooltip title="Driver acceptance status for the shift">
            <span style={{ fontWeight: '600' }}>Acceptance</span>
          </Tooltip>
        ),
        renderCell: (params: any) => {
          const { value } = params;

          return <span>{getDriverAcceptance(value, true)}</span>;
        }
      }
    ] : []),
    ...((shiftStatus === 3) ? [
      {
        field: 'timesheetStatus',
        headerName: 'Timesheet?',
        flex: 1,
        minWidth: 100,
        filterable: false,
        renderHeader: () => (
          <Tooltip title="Status of timesheet submission">
            <span style={{ fontWeight: '600' }}>Timesheet?</span>
          </Tooltip>
        ),
        renderCell: (params: any) => {
          const { value, row } = params;
          return (
            <Tooltip title={getTimsheetStatusTooltip(row)}>
              <span>{value}</span>
            </Tooltip>
          );
        }
      }
    ] : []),
    ...((true) ? [
      {
        field: 'checkinStatus',
        headerName: 'Checked In',
        flex: 1,
        minWidth: 100,
        filterable: false,
        renderCell: (params: any) => {
          const { value, row } = params;
          return (
            <Tooltip title={getTimsheetStatusTooltip(row)}>
              <span>{value}</span>
            </Tooltip>
          );
        }
      }
    ] : []),
    ...((true) ? [
      {
        field: 'estimatedVehicleReturnLocal',
        headerName: 'Est. Vehicle Return',
        flex: 1,
        minWidth: 90,
        maxWidth: 100,
        type: 'date',
        filterable: false,
        renderHeader: () => (
          <Tooltip title="Estimated vehicle return time (local)">
            <span style={{ fontWeight: '600' }}>Est. Truck Return</span>
          </Tooltip>
        ),
        renderCell: (params: any) => {
          const { value } = params;
          return (
            <Tooltip title={formatDateToCalendarTime(value, true) || 'Not set'}>
              <span>
                {value ? formatDateToOnlyTime(value, true) : '--'}
              </span>
            </Tooltip>
          );
        }
      }
    ] : []),
    ...((true) ? [
      {
        field: 'checkinPhoto',
        headerName: 'WooliesGo Checkin Photo',
        width: 100,
        filterable: false,
        renderHeader: () => (
          <Tooltip title="WooliesGo checkin photo">
            <span style={{ fontWeight: '600' }}>WooliesGo Checkin</span>
          </Tooltip>
        ),
        renderCell: (params: any) => {
          const { value, row } = params;

          return (
            <Tooltip title={getTimsheetStatusTooltip(row)}>
              {value ? (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenImage(value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleOpenImage(value)}
                  style={{ cursor: 'pointer', display: 'inline-block' }}
                >
                  <img
                    src={value}
                    alt="Check-in"
                    style={{ width: 50, height: 50, borderRadius: 4 }}
                  />
                </div>
              ) : (
                <span>No Image</span>
              )}
            </Tooltip>
          );
        }
      }
    ] : []),
    {
      field: 'routeId',
      headerName: 'Route ID',
      flex: 1,
      minWidth: 100,
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Unique identifier for the delivery route">
          <span style={{ fontWeight: '600' }}>Route ID</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        const displayValue = value && value.length > 6
          ? `${value.slice(0, 3)} ... ${value.slice(-3)}`
          : value;

        return (
          <Tooltip title={value}>
            <span>{displayValue}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 80,
      pinned: 'right',
      renderCell: (params: any) => {
        const { row } = params;
        return (
          <>
            <IconButton data-auto-id="shiftDataGridEditRowButton" size="medium" aria-label="edit" onClick={() => onRowClick(row)}>
              <EditIcon />
            </IconButton>
          </>
        );
      }
    },
  ];

  const onSearchTextChange = (event: any) => {
    onFilterByText(event?.target?.value);
  };

  const onFilterByChange = (event: any) => {
    onFilterByWarning(event.target.value);
  };

  const filterOptions = [
    { value: 'all', text: 'Show All' },
    { value: 'breach', text: 'Breach' }
  ];

  const renderEachMobileCard = (row: FormattedShift) => (
    <Card key={row.id} sx={{ marginBottom: '10px' }} variant="outlined">
      <CardHeader
        avatar={
          <UserCheckIcon />
        }
        action={
          (
            <>
              {
                isDriver(roles) && shiftStatus === 1 && row.isNew && (
                  <Typography variant="caption" sx={{ backgroundColor: 'green', color: 'white', borderRadius: '5px', lineHeight: 1, padding: '5px', fontSize: '0.75rem' }}>New</Typography>
                )
              }
              <IconButton aria-label="edit">
                <EditIcon />
              </IconButton>
            </>
          )
        }
        title={row.userName || row.userEmail}
        subheader={row.shiftStatus}
        onClick={() => onRowClick(row)}
      />
      <Divider />
      <CardContent>
        <Grid container>
          {
            shiftStatus === 3 && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <b>Timesheet Submitted?:&nbsp;</b>
                  {row.timesheetStatus}
                </Typography>
              </Grid>
            )
          }
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Driver Acceptance:&nbsp;</b>
              {getDriverAcceptance(row.userHasAccepted, true)}
            </Typography>
          </Grid>
          {
            row.userExtDriverId && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <b>Ext Driver Id:</b> {row.userExtDriverId}
                </Typography>
              </Grid>
            )
          }
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Vehicle:</b> {row.vehicleRego || `${row.unlistedVehicleRego} (Not listed)`}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Store/Jobsite:</b> {getJobSiteName(row?.jobsite)}
            </Typography>
          </Grid>
          {
            row.scheduledStartUTC && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <b>Scheduled Start:</b> {formatDateToLongDate(row.scheduledStartUTC, true)}
                </Typography>
              </Grid>
            )
          }
          {
            row.scheduledEndUTC && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <b>Scheduled End:</b> {formatDateToLongDate(row.scheduledEndUTC, true)}
                </Typography>
              </Grid>
            )
          }
          {
            row.startedOnUTC && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <b>Actual Start:</b> {formatDateToLongDate(row.startedOnUTC, true)}
                </Typography>
              </Grid>
            )
          }
          {
            row.endedOnUTC && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <b>Actual End:</b> {formatDateToLongDate(row.endedOnUTC, true)}
                </Typography>
              </Grid>
            )
          }
          {(true) && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <b>Checked In:</b> {row.checkinStatus}
              </Typography>

              {/* Show Check-in Photo */}
              {row.checkinPhoto && (
                <Tooltip title="Click to view full image">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleOpenImage(row.checkinPhoto)}
                    onKeyPress={(e) => e.key === 'Enter' && handleOpenImage(row.checkinPhoto)}
                    style={{ cursor: 'pointer', marginTop: 8, display: 'inline-block' }}
                  >
                    <img
                      src={row.checkinPhoto}
                      alt="Check-in"
                      style={{ width: 80, height: 80, borderRadius: 6 }}
                    />
                  </div>
                </Tooltip>
              )}
            </Grid>
          )}
          {
            row.estimatedVehicleReturnLocal && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <b>Est. Vehicle Return:</b> {formatDateToLongDate(row.estimatedVehicleReturnLocal, true)}
                </Typography>
              </Grid>
            )
          }
          {
            (row.startOdometerKms || row.startOdometerKms === 0) && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <b>Start Odometer:</b> {row.startOdometerKms} kms
                </Typography>
              </Grid>
            )
          }
          {
            (row.endOdometerKms || row.endOdometerKms === 0) && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <b>End Odometer:</b> {row.endOdometerKms} kms
                </Typography>
              </Grid>
            )
          }
        </Grid>
        {
          row.breach?.type && (
            <Grid item xs={12} sx={{ paddingTop: 2 }}>
              <Alert severity={row.breach.type}>{row.breach.message}</Alert>
            </Grid>
          )
        }
      </CardContent>
    </Card>
  );

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
          }}
          onClick={handleClose}
        >
          <img src={selectedImage} alt="Check-in Full View" style={{ maxWidth: '90%', maxHeight: '90%', borderRadius: 8 }} />
        </Box>
      </Modal>
      <Hidden mdDown>
        <Toolbar style={{ justifyContent: 'flex-end' }}>
          {
            selectableRows && (
              <MultiRowActionButton
                selectedRowsCount={selectionModel?.length ?? 0}
                options={
                  [
                    {
                      label: 'Delete',
                      onClick: () => onBulkDelete(selectionModel)
                    }
                  ]
                }
                disabled={!selectionModel?.length}
              />
            )
          }
          <div style={{ flex: '1' }} />
          <Box
            sx={{
              mt: 3,
              mb: 3,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1.5,
              position: 'relative',
            }}
          >
            {[
              {
                label: 'AM',
                value: shifts.filter((x) => x.amOrPm === 'AM').length,
                color: '#42A5F5', // Sky blue (morning freshness)
                icon: <ScheduleIcon style={{ color: '#42A5F5' }} fontSize="small" />
              },
              {
                label: 'PM',
                value: shifts.filter((x) => x.amOrPm === 'PM').length,
                color: '#EF5350', // Soft red (evening warmth)
                icon: <ScheduleIcon style={{ color: '#EF5350' }} fontSize="small" />
              },
            ].map((item, index) => (
              <Chip
                key={index}
                clickable
                onClick={() => setSelectedSummary(item.label)}
                onDelete={selectedSummary === item.label && item.label !== 'Total' ? () => setSelectedSummary('') : undefined}
                deleteIcon={selectedSummary === item.label && item.label !== 'Total' ? <CancelIcon fontSize="small" /> : undefined}
                label={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {item.icon}
                    <Box>
                      <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                        {item.value}
                      </Typography>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontWeight: selectedSummary === item.label ? 700 : 400,
                          // textDecoration: selectedSummary === item.label ? 'underline' : 'none',
                          cursor: 'pointer',
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                  </Box>
                )}
                sx={{
                  height: 36,
                  borderRadius: 18,
                  bgcolor: `${item.color}4D`,
                  border: `1px solid ${item.color}80`,
                  '& .MuiChip-label': {
                    px: 1.5,
                    py: 0.5
                  },
                  '&:hover': {
                    bgcolor: `${item.color}12`,
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s ease',
                  boxShadow: selectedSummary === item.label ? `0 0 0 2px ${item.color}80` : undefined,
                  fontWeight: selectedSummary === item.label ? 700 : 400,
                }}
                // color={selectedSummary === item.label ? 'primary' : 'default'}
                variant={selectedSummary === item.label ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <GlobalSearch searchedText={searchedText} onSearchTextChange={onSearchTextChange} showFilterByColumn columns={columns} filterByColumn={filterByCol} onFilterByColumnChange={onChangeFilterByColumn} width="400px" />
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {
            shiftStatus === 2 && (
              <FilterBy options={filterOptions} value={selectedWarning} onChange={onFilterByChange} width="225px" />
            )
          }
        </Toolbar>

        {/* Filter rows by selected summary chip, including AM/PM, Breached, and High Risk */}
        {(() => {
          let filteredRows = shifts;
          if (selectedSummary === 'AM') {
            filteredRows = shifts.filter((x) => x.amOrPm === 'AM');
          } else if (selectedSummary === 'PM') {
            filteredRows = shifts.filter((x) => x.amOrPm === 'PM');
          }

          return (
            <>
              <div data-auto-id="shiftDataGrid">
                <Table
                  rows={filteredRows}
                  columns={columns}
                  onRowDoubleClick={({ row }) => onRowClick(row)}
                  totalRows={totalRowCount}
                  page={currentPage}
                  checkboxSelection={selectableRows}
                  onPageChange={onPageChange}
                  sortModel={sortModel}
                  onSortModelChange={onSort}
                  getRowClassName={(params: any) => params?.row.breach?.type && 'error'}
                  selectionModel={selectionModel}
                  onSelectionModelChange={(selectedIds) => onSelectionModelChange(shifts, selectedIds)}
                />
              </div>
            </>
          );
        })()}
      </Hidden>
      <Hidden mdUp>
        <Toolbar sx={{ flexDirection: 'column' }}>
          {
            shiftStatus === 2 && (
              <div style={{ width: '100%', padding: '5px 0' }}>
                <FilterBy options={filterOptions} value={selectedWarning} onChange={onFilterByChange} />
              </div>
            )
          }
          <div style={{ width: '100%', padding: '5px 0' }}>
            <GlobalSearch searchedText={searchedText} onSearchTextChange={onSearchTextChange} showFilterByColumn columns={columns} filterByColumn={filterByCol} onFilterByColumnChange={onChangeFilterByColumn} />
          </div>
        </Toolbar>
        <MobileDataList
          rows={shifts}
          renderEachRow={renderEachMobileCard}
          totalRows={totalRowCount}
          page={currentPage}
          onPageChange={onPageChange}
        />
      </Hidden>
    </>
  );
};

export default ShiftTable;
